import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState } from "@/components/common/EmptyState";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const label = (e) => e.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const summarise = (d) => Object.entries(d || {}).map(([k, v]) => `${k.replace(/_/g, " ")}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
const PAGE = 50;

export default function AuditAdmin() {
  const [event, setEvent] = useState("all");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async (skip = 0) => {
    try {
      const params = { skip, limit: PAGE, ...(event !== "all" && { event }) };
      const { data: d } = await api.get("/admin/audit", { params });
      setData((prev) => (skip && prev ? { ...d, items: [...prev.items, ...d.items] } : d));
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [event]);
  useEffect(() => { setData(null); load(0); }, [load]);

  return (
    <div data-testid="admin-audit">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="h-10 w-[240px] rounded-xl bg-card" aria-label="Filter by event" data-testid="audit-event-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">All events</SelectItem>
            {(data?.events || []).map((e) => <SelectItem key={e} value={e}>{label(e)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="secondary" onClick={() => downloadFile("/admin/export/audit", "audit.csv").catch((e) => toast.error(errorMessage(e)))} data-testid="audit-export-button"><Download className="!h-4 !w-4" /> Export CSV</Button>
      </div>
      {error ? <ErrorState message={error} onRetry={() => load(0)} /> : !data ? <Skeleton className="h-64 rounded-xl" /> : (
        <>
          <div className="card-elevated overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Event</TableHead><TableHead>By</TableHead><TableHead>Target</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No events recorded yet.</TableCell></TableRow>}
                {data.items.map((a) => (
                  <TableRow key={a.id} data-testid="audit-row">
                    <TableCell className="whitespace-nowrap text-sm">{formatDateTime(a.at)}</TableCell>
                    <TableCell className="text-sm font-medium">{label(a.event)}</TableCell>
                    <TableCell className="text-sm">{a.actor_name || "System"}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{a.target}</TableCell>
                    <TableCell className="max-w-[360px] text-xs text-muted-foreground">{summarise(a.detail)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="audit-count">Showing {data.items.length} of {data.total}</span>
            {data.items.length < data.total && <Button variant="secondary" onClick={() => load(data.items.length)} data-testid="audit-load-more">Load more</Button>}
          </div>
        </>
      )}
    </div>
  );
}
